import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import shallow from 'zustand/shallow'
import Mapbox, { useMapStore } from '@hyperobjekt/mapbox'

import useStore from './../store'
import {
  getRoundedValue,
  isWkkfView,
  useDebounce,
} from './../utils'
import {
  DEFAULT_ROUTE,
  DEFAULT_VIEWPORT,
  OPTIONS_MAP,
  ROUTE_VIEW,
  ROUTE_ACTIVE_SHAPE,
  ROUTE_ACTIVE_YEAR,
  ROUTE_LOAD_YEARS,
  ROUTE_ACTIVE_POINTS,
  ROUTE_RATING_TYPE,
  ROUTE_METRIC,
  ROUTE_NORM,
  ROUTE_SET,
  ROUTE_GEO,
  ROUTE_ACTIVE_REDLINING,
  getDefaultRoute,
  DEFAULT_LOAD_YEARS,
} from './../../../../constants/map'
import { validateRouteOption } from './utils/utils'
const poi_data = require('../../../../constants/poi_data.json')

/**
 * Get a route parameters object based on the string
 * @param {string} path
 * @returns {object} e.g. { region: 'counties', metric: 'avg', ... }
 */
export const getParamsFromPathname = (path, routeVars) => {
  // console.log('getParamsFromPathname()')
  // strip starting "#" and "/" chars
  const route = path.replace(/^#\/+/g, '')
  // Construct object from hash
  return route.split('/').reduce(
    (acc, curr, i) => ({
      ...acc,
      [routeVars[i]]:
        ['zoom', 'lat', 'lng'].indexOf(routeVars[i]) > -1
          ? parseFloat(curr)
          : curr,
    }),
    {},
  )
}

export const getStrippedRoute = route =>
  route.replace(/^#[/]+/g, '').replace(/\/$/g, '')

export const isEmptyRoute = route =>
  getStrippedRoute(route).length === 0

/**
 * Verify that the route value is valid.
 * @param  {String} route
 * @param  {String} value
 * @return {Boolean}
 */
const isRouteOptionValid = (item, value) => {
  const optionsItem = OPTIONS_MAP[item]
  const isValid = validateRouteOption(optionsItem, value)
  return isValid
}

const isLatLngValid = (lat, lng) => {
  // console.log('isLatLngValid()')
  // Make sure they're inside the bounds.
  if (!lat && !lng) return true
  lat = Number(lat)
  lng = Number(lng)
  let isValid = true
  if (
    lat < DEFAULT_VIEWPORT.maxBounds[0][1] ||
    lat > DEFAULT_VIEWPORT.maxBounds[1][1]
  ) {
    // console.log('lat out of bounds')
    isValid = false
  }
  if (
    lng < DEFAULT_VIEWPORT.maxBounds[0][0] ||
    lng > DEFAULT_VIEWPORT.maxBounds[1][0]
  ) {
    // console.log('lng out of bounds')
    isValid = false
  }
  return isValid
}

const isZoomValid = zoom => {
  // console.log('isZoomValid, ', zoom)
  if (!zoom) return true
  // Make sure it's within the zoom min and max.
  return (
    zoom >= DEFAULT_VIEWPORT.minZoom &&
    zoom <= DEFAULT_VIEWPORT.maxZoom
  )
}

/**
 * Validates all route params
 * @param  {Object}  params [description]
 * @return {Boolean}        [description]
 */
const isRouteValid = params =>
  Object.keys(OPTIONS_MAP).every(el =>
    isRouteOptionValid(el, params[el]),
  ) &&
  isLatLngValid(params.lat, params.lng) &&
  isZoomValid(params.zoom)

const RouteManager = props => {
  // console.log('RouteManager!!!!!')
  // track if initial route has loaded
  const isLoaded = useRef(false)
  // Values from store.
  const {
    activeView,
    activeShape,
    activeYear,
    dataVersion,
    loadYears,
    activePointLayers,
    activeRatingType,
    activeMetric,
    activeNorm,
    activeGeoType,
    showDensityOverlays,
    redliningActive,
    activeRedliningTypes,
    setStoreValues,
    viewport,
    setViewport,
    shareHash,
  } = useStore(
    state => ({
      activeView: state.activeView,
      activeShape: state.activeShape,
      activeYear: state.activeYear,
      dataVersion: state.dataVersion,
      loadYears: state.loadYears,
      activePointLayers: state.activePointLayers,
      activeRatingType: state.activeRatingType,
      activeMetric: state.activeMetric,
      activeNorm: state.activeNorm,
      activeGeoType: state.activeGeoType,
      showDensityOverlays: state.showDensityOverlays,
      redliningActive: state.redliningActive,
      activeRedliningTypes: state.activeRedliningTypes,
      setStoreValues: state.setStoreValues,
      viewport: state.viewport,
      setViewport: state.setViewport,
      shareHash: state.shareHash,
    }),
    shallow,
  )

  const setMapViewport = useMapStore(
    state => state.setViewport,
  )

  /**
   * Returns a hash based on state
   * @return {String} [description]
   */
  const getHashFromState = () => {
    const pointLayers = showDensityOverlays
      ? activePointLayers
      : []
    const redliningTypes = redliningActive
      ? activeRedliningTypes
      : []

    const hash = `${activeView}/${activeGeoType}/${activeShape}/${activeYear}/${loadYears.toString()}/${pointLayers.toString()}/${activeRatingType}/${activeMetric}/${activeNorm}/${dataVersion}/${getRoundedValue(
      viewport.latitude,
      4,
    )}/${getRoundedValue(
      viewport.longitude,
      4,
    )}/${getRoundedValue(
      viewport.zoom,
      2,
    )}/${redliningTypes.sort().toString()}`
    return hash
  }

  // get the route params based on current view
  const route = getHashFromState()
  // debounce the route so it updates every 1 second max
  const debouncedRoute = useDebounce(route, 500)

  /**
   * Sets state from route params on load
   * @param {[type]} params [description]
   */
  const setStateFromHash = params => {
    // console.log('setStateFromHash(), ', params)
    const wkkfViewActive = isWkkfView(params[ROUTE_VIEW])

    if (params.hasOwnProperty(ROUTE_VIEW)) {
      const activeView = params[ROUTE_VIEW]
      const values = { activeView }

      if (wkkfViewActive) {
        const activePOI = activeView.replace(/\D/g, '')
        // if POI is selected, activate it
        if (activePOI) {
          values.POIModeActive = true
          values.activePOI = activePOI

          // if we're in state or metro view, activate the one the POI corresponds to
          const norm = params[ROUTE_NORM]
          if (norm === 's' || norm === 'm') {
            const activeRegion = poi_data.sites.find(
              s => s.id === values.activePOI,
            )[norm === 's' ? 'state_fips' : 'metro_fips']
            values.activeRegion = activeRegion
          }
        }
      }
      setStoreValues(values)
    }
    if (params.hasOwnProperty(ROUTE_GEO)) {
      setStoreValues({ activeGeoType: params[ROUTE_GEO] })
    }
    if (params.hasOwnProperty(ROUTE_ACTIVE_SHAPE)) {
      setStoreValues({
        activeShape: Number(params[ROUTE_ACTIVE_SHAPE]),
      })
    }
    // TODO: revert when WKKF uses same years
    // if (params.hasOwnProperty(ROUTE_ACTIVE_YEAR)) {
    if (
      params.hasOwnProperty(ROUTE_ACTIVE_YEAR) &&
      DEFAULT_LOAD_YEARS.includes(
        params[ROUTE_ACTIVE_YEAR],
      ) &&
      !wkkfViewActive
    ) {
      setStoreValues({
        activeYear: params[ROUTE_ACTIVE_YEAR],
      })
    }
    // TODO: revert when WKKF uses same years
    // if (params.hasOwnProperty(ROUTE_LOAD_YEARS)) {
    if (
      params.hasOwnProperty(ROUTE_LOAD_YEARS) &&
      !wkkfViewActive
    ) {
      let loadYears = params[ROUTE_LOAD_YEARS].split(
        ',',
      ).filter(y => DEFAULT_LOAD_YEARS.includes(y))
      if (loadYears.length === 0)
        loadYears = DEFAULT_LOAD_YEARS
      setStoreValues({
        // loadYears: params[ROUTE_LOAD_YEARS].split(','),
        loadYears,
      })
    }
    if (params.hasOwnProperty(ROUTE_ACTIVE_POINTS)) {
      // If empty string, set empty array.
      if (params[ROUTE_ACTIVE_POINTS] === '') {
        // don't override the DEFAULT_ACTIVE_POINTS (all)
        // setStoreValues({
        //   activePointLayers: [],
        // })
      } else {
        setStoreValues({
          activePointLayers: params[
            ROUTE_ACTIVE_POINTS
          ].split(','),
          // TODO: tear out ?
          legendControlActive: true,
          showDensityOverlays: true,
        })
      }
    }
    if (params.hasOwnProperty(ROUTE_ACTIVE_REDLINING)) {
      if (params[ROUTE_ACTIVE_REDLINING] === '') {
        setStoreValues({
          activeRedliningTypes: [],
        })
      } else {
        setStoreValues({
          activeRedliningTypes: params[
            ROUTE_ACTIVE_REDLINING
          ].split(',').sort(),
          redliningActive: true,
        })
      }
    }
    if (params.hasOwnProperty(ROUTE_RATING_TYPE)) {
      setStoreValues({
        activeRatingType: params[ROUTE_RATING_TYPE],
      })
    }
    if (params.hasOwnProperty(ROUTE_METRIC)) {
      setStoreValues({ activeMetric: params[ROUTE_METRIC] })
    }
    if (params.hasOwnProperty(ROUTE_NORM)) {
      setStoreValues({ activeNorm: params[ROUTE_NORM] })
    }

    let resetViewport = false
    if (!!params.lat && !!params.lng) {
      viewport.latitude = Number(params.lat)
      viewport.longitude = Number(params.lng)
      resetViewport = true
    }
    if (!!params.zoom) {
      viewport.zoom = Number(params.zoom)
      resetViewport = true
    }
    if (!!resetViewport) {
      // console.log('resetting viewport')
      setViewport(viewport)
      setMapViewport(viewport)
    }
    setStoreValues({ initialStateSetFromHash: true })
  }

  useEffect(() => {
    if (isLoaded.current) {
      // When hash changes, if route is valid, update route for sharing.
      window.addEventListener('hashchange', () => {
        // console.log('hashchange')
        const path = window.location.hash
        // Construct params object from hash.
        const params = getParamsFromPathname(
          path,
          ROUTE_SET,
        )
        if (
          !isEmptyRoute(path) &&
          isRouteValid(params, ROUTE_SET) &&
          path !== shareHash
        ) {
          // console.log('updating hash')
          setStoreValues({
            shareHash: window.location.hash,
          })
        }
      })
    }
  }, [isLoaded.current])

  // update the hash when debounced route changes
  useEffect(() => {
    // only change the hash if the initial route has loaded
    if (isLoaded.current) {
      // window.location.hash = '#/' + debouncedRoute
      // console.log('Route change')
      window.history.replaceState(
        { hash: '#/' + debouncedRoute },
        'Explorer state update',
        window.location.origin +
          window.location.pathname +
          '#/' +
          debouncedRoute,
      )
      localStorage.setItem(
        'ddk_hash',
        '#/' + debouncedRoute,
      )
      setStoreValues({ shareHash: '#/' + debouncedRoute })
    }
  }, [debouncedRoute])

  // load the route when the application mounts
  useEffect(() => {
    async function loadRoute() {
      // console.log('loadRoute')
      isLoaded.current = true
      // Get path.
      const path = window.location.hash

      if (path.startsWith('#/wkkf-')) {
        // handle POI shortened URLs
        const poi = poi_data.sites.find(({ slug }) => {
          return (
            slug ===
            path.replace('#/wkkf-', '').toLowerCase()
          )
        })
        // loading a particular POI
        if (!!poi) {
          const route = getDefaultRoute({
            view: 'wkkf' + poi.id,
          })
          const params = getParamsFromPathname(
            route,
            ROUTE_SET,
          )
          setStoreValues({ loadFromPOIUrl: true })
          setStateFromHash(params)
          return
        }
      }
      // Construct params object from hash.
      const params = getParamsFromPathname(path, ROUTE_SET)
      const localStorageHash = localStorage.getItem(
        'ddk_hash',
      )
      if (
        !isEmptyRoute(path) &&
        isRouteValid(params, ROUTE_SET)
      ) {
        // Update state based on params
        // console.log(
        //   'hash is valid, setting state from hash.',
        // )
        setStateFromHash(params)
      } else if (!!localStorageHash) {
        if (localStorageHash.length > 0) {
          // console.log('localStorage exists')
          const lsparams = getParamsFromPathname(
            localStorageHash,
            ROUTE_SET,
          )
          if (isRouteValid(lsparams, ROUTE_SET)) {
            // console.log(
            //   'localStorage is valid, setting state from localStorage',
            // )
            setStateFromHash(lsparams)
          } else {
            // If a hash was passed in, but there is
            // no valid hash in path or localStorage,
            // set the hash from the default route and proceed.
            setStateFromHash(DEFAULT_ROUTE)
            setStoreValues({
              showIntroModal: true,
              initialStateSetFromHash: true,
            })
          }
        }
      }
      if (isEmptyRoute(path) && !localStorageHash) {
        // console.log('showing intro modal')
        setStoreValues({
          showIntroModal: true,
          initialStateSetFromHash: true,
        })
      }
    }
    loadRoute()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // this component doesn't render anything
  return null
}

RouteManager.propTypes = {
  routeSet: PropTypes.array, // Constants listing params and allowable options.
}

export default RouteManager
